import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import { getWalletEmployeesPaginated, updateWalletEmployee } from '../../api/endpoints/wallet';
import { getErrorMessage } from '../../utils/errorHandler';
import { AlertMessages } from '../AlertMessages';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

/** Formata dígitos para exibição de CPF (11 dígitos máx.) */
function formatCpfDisplay(value) {
  const numbers = String(value || '').replace(/\D/g, '').slice(0, 11);
  if (!numbers) return '';
  return numbers
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
}

export const WalletEmployeesListTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [alertState, setAlertState] = useState({
    show: false,
    type: 'success',
    message: '',
  });
  const [updatingId, setUpdatingId] = useState(null);
  const searchDebounceRef = useRef(null);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState(null);
  const [editName, setEditName] = useState('');
  const [editCpf, setEditCpf] = useState('');
  const [newCardCode, setNewCardCode] = useState('');
  const [newUserLogin, setNewUserLogin] = useState('');

  const showAlert = (type, message) => {
    setAlertState({
      show: true,
      type,
      message,
    });
  };

  const fetchData = async (override = {}) => {
    const currentPage = override.page !== undefined ? override.page : page;
    const currentPageSize = override.pageSize || pageSize;
    const currentSearch = override.search !== undefined ? override.search : search;
    const currentStatus = override.status || status;

    try {
      setLoading(true);
      const data = await getWalletEmployeesPaginated({
        page: currentPage + 1,
        pageSize: currentPageSize,
        search: currentSearch || undefined,
        status: currentStatus,
      });

      setRows(data.employees || []);
      setTotal(data.pagination?.total || 0);
    } catch (error) {
      showAlert('error', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, status]);

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setPage(0);
      fetchData({ page: 0, search: value });
    }, 400);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setPageSize(value);
    setPage(0);
  };

  const handleToggleStatus = async (employee) => {
    const newStatus = !employee.isActive;
    const confirmed = window.confirm(
      `Tem certeza que deseja ${newStatus ? 'ativar' : 'desativar'} o colaborador "${employee.name}"?`
    );
    if (!confirmed) return;

    try {
      setUpdatingId(employee.id);
      await updateWalletEmployee(employee.id, { isActive: newStatus });
      showAlert('success', `Colaborador ${newStatus ? 'ativado' : 'desativado'} com sucesso.`);
      fetchData();
    } catch (error) {
      showAlert('error', getErrorMessage(error));
    } finally {
      setUpdatingId(null);
    }
  };

  const openEditDialog = (employee) => {
    setEditEmployee(employee);
    setEditName(employee.name || '');
    setEditCpf(formatCpfDisplay(employee.cpf || ''));
    setNewCardCode(employee.employeeCode || '');
    setNewUserLogin(employee.userLogin || '');
    setEditDialogOpen(true);
  };

  const handleConfirmEditCard = async () => {
    if (!editEmployee) return;

    const nameTrim = (editName || '').trim();
    if (!nameTrim) {
      showAlert('error', 'Informe o nome do colaborador.');
      return;
    }

    const trimmed = (newCardCode || '').trim();

    if (!trimmed) {
      showAlert('error', 'Informe o novo código/UUID do cartão.');
      return;
    }

    const cpfDigits = (editCpf || '').replace(/\D/g, '');
    if (cpfDigits.length > 0 && cpfDigits.length !== 11) {
      showAlert('error', 'CPF deve ter 11 dígitos ou ficar vazio.');
      return;
    }

    try {
      setUpdatingId(editEmployee.id);
      await updateWalletEmployee(editEmployee.id, {
        name: nameTrim,
        cpf: cpfDigits.length ? cpfDigits : '',
        employeeCode: trimmed,
        userLogin: (newUserLogin || '').trim() || null,
      });
      showAlert('success', 'Dados do colaborador atualizados com sucesso.');
      setEditDialogOpen(false);
      setEditEmployee(null);
      fetchData();
    } catch (error) {
      showAlert('error', getErrorMessage(error));
    } finally {
      setUpdatingId(null);
    }
  };

  const emptyMessage = useMemo(() => {
    if (loading) return 'Carregando colaboradores...';
    if (search) return 'Nenhum colaborador encontrado para o filtro informado.';
    if (status === 'active') return 'Nenhum colaborador ativo com NFC cadastrado.';
    if (status === 'inactive') return 'Nenhum colaborador inativo com NFC cadastrado.';
    return 'Nenhum colaborador com NFC cadastrado.';
  }, [loading, search, status]);

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 3,
          border: '1px solid #e0e0e0',
          bgcolor: '#fafafa',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0b4f6c' }}>
              Colaboradores com Cartão NFC
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Consulte, ative/desative e atualize o cartão dos colaboradores de forma rápida.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <TextField
              size="small"
              placeholder="Buscar por nome..."
              value={search}
              onChange={handleSearchChange}
              sx={{ minWidth: 220, bgcolor: 'white' }}
            />

            <TextField
              select
              size="small"
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 150, bgcolor: 'white' }}
            >
              <MenuItem value="active">Ativos</MenuItem>
              <MenuItem value="inactive">Inativos</MenuItem>
              <MenuItem value="all">Todos</MenuItem>
            </TextField>

            <Tooltip title="Recarregar lista">
              <span>
                <IconButton onClick={() => fetchData()} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ maxHeight: 460, borderRadius: 2, bgcolor: '#fff' }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>CPF</TableCell>
                <TableCell>Login do Usuário</TableCell>
                <TableCell>Código do Cartão</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((employee) => (
                  <TableRow key={employee.id} hover>
                    <TableCell>{employee.name}</TableCell>
                    <TableCell>{employee.cpf || 'Não informado'}</TableCell>
                    <TableCell>{employee.userLogin || 'Não vinculado'}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}
                      >
                        {employee.employeeCode}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={employee.isActive ? 'Ativo' : 'Inativo'}
                        color={employee.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant={employee.isActive ? 'outlined' : 'contained'}
                          color={employee.isActive ? 'warning' : 'success'}
                          onClick={() => handleToggleStatus(employee)}
                          disabled={updatingId === employee.id}
                          sx={{ minWidth: 90 }}
                        >
                          {employee.isActive ? 'Desativar' : 'Ativar'}
                        </Button>
                        <Tooltip title="Editar nome, CPF, cartão e login">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openEditDialog(employee)}
                              disabled={updatingId === employee.id}
                              aria-label="Alterar cartão"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          labelRowsPerPage="Itens por página"
        />
      </Paper>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar colaborador</DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {editEmployee && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Referência no cadastro — código NFC e login atual:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cartão: {editEmployee.employeeCode || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Login: {editEmployee.userLogin || 'Não vinculado'}
              </Typography>
            </Box>
          )}

          <TextField
            variant="outlined"
            label="Nome completo"
            fullWidth
            margin="normal"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            placeholder="Nome como aparece na cantina / relatórios"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            variant="outlined"
            label="CPF"
            fullWidth
            margin="normal"
            value={editCpf}
            onChange={(e) => setEditCpf(formatCpfDisplay(e.target.value))}
            placeholder="000.000.000-00"
            helperText="Opcional. Deixe vazio para remover o CPF cadastrado."
            InputLabelProps={{ shrink: true }}
            inputProps={{ inputMode: 'numeric', maxLength: 14 }}
          />
          <TextField
            variant="outlined"
            label="Código / UUID do cartão NFC"
            fullWidth
            margin="normal"
            value={newCardCode}
            onChange={(e) => setNewCardCode(e.target.value)}
            placeholder="Leia o novo cartão NFC ou digite o UUID"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            variant="outlined"
            label="Login do usuário (app)"
            fullWidth
            margin="normal"
            value={newUserLogin}
            onChange={(e) => setNewUserLogin(e.target.value)}
            placeholder="Ex: ivanildo_candido_bezerra"
            helperText="Deixe vazio para remover o vínculo com users.login."
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={updatingId != null}>
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmEditCard}
            variant="contained"
            disabled={updatingId != null}
            sx={{ bgcolor: '#1e958c', '&:hover': { bgcolor: '#0b4f6c' } }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <AlertMessages
        show={alertState.show}
        typeMessage={alertState.type}
        contentMessage={alertState.message}
        setShow={(show) => setAlertState((prev) => ({ ...prev, show }))}
      />
    </>
  );
};
